import React, { useState, useEffect } from "react";
import { Tag } from "antd";
import { Check, RotateCcw, ArrowRight, Lock, CheckCircle2, PlayCircle } from "lucide-react";

const LessonPlayerView = ({
  lesson,
  lessonIndex,
  totalLessons,
  lessons = [],
  watchedLessons = {},
  lessonsCompletedCount = 0,
  isWatched,
  hasQuiz = false,
  isQuizCompleted = false,
  iframeRef,
  onMarkAsWatched,
  onSelectLesson,
  onPrevious,
  onNext,
}) => {
  const isLessonWatched = (item, idx) => {
    if (!item && idx === undefined) return false;
    return Boolean(item && watchedLessons[item.id]) || Boolean(watchedLessons[idx]) || (Number(lessonsCompletedCount) > idx);
  };
  const effectiveIsWatched = isWatched || isLessonWatched(lesson, lessonIndex);
  const isLastLesson = lessonIndex === totalLessons - 1;
  const isLessonOne = lessonIndex === 0;

  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    setSecondsLeft(0);
  }, [lessonIndex, effectiveIsWatched, isLessonOne]);

  const formatTime = (secs) => {
    const mins = Math.floor(secs / 60);
    const rem = secs % 60;
    return `${mins}:${rem < 10 ? "0" : ""}${rem}`;
  };

  const isLessonOneLocked = isLessonOne && !effectiveIsWatched && secondsLeft > 0;

  return (
    <div className="space-y-4 text-left">
      {/* Lesson Selector Step Chips */}
      {lessons.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {lessons.map((item, idx) => {
            const isItemWatched = isLessonWatched(item, idx);
            const isCurrent = idx === lessonIndex;
            const isUnlocked = idx === 0 || isLessonWatched(lessons[idx - 1], idx - 1);

            return (
              <button
                key={item.id || idx}
                type="button"
                disabled={!isUnlocked}
                onClick={() => isUnlocked && onSelectLesson && onSelectLesson(idx)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-gilroyBold transition-all flex-shrink-0 border cursor-pointer ${
                  isCurrent
                    ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                    : isItemWatched
                    ? "bg-white text-gray-800 border-gray-200 hover:bg-gray-50"
                    : isUnlocked
                    ? "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    : "bg-gray-50 text-gray-400 border-gray-200/60 opacity-60 cursor-not-allowed"
                }`}
              >
                {isItemWatched ? (
                  <Check className="w-3.5 h-3.5 text-emerald-600" />
                ) : isCurrent ? (
                  <PlayCircle className="w-3.5 h-3.5 text-white" />
                ) : isUnlocked ? (
                  <span className="w-4 h-4 rounded-full bg-gray-100 text-gray-700 text-[11px] flex items-center justify-center font-bold">
                    {idx + 1}
                  </span>
                ) : (
                  <Lock className="w-3.5 h-3.5 text-gray-400" />
                )}
                <span>Lesson {idx + 1}</span>
                {isItemWatched && !isCurrent && (
                  <span className="text-[11px] bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded-full font-gilroyBold">
                    Done
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Lesson Header Card */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="bg-gray-900 text-white font-gilroyBold text-sm w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-xs">
            {lessonIndex + 1}
          </span>
          <div>
            <h2 className="font-gilroyBold text-[15px] md:text-base text-gray-900 m-0">
              Lesson {lessonIndex + 1} of {totalLessons}
            </h2>
            <span className="text-gray-500 text-[13px] block mt-0.5">
              {effectiveIsWatched
                ? "Video Completed — Ready for Next Lesson"
                : isLessonOneLocked
                ? `Complete button activates in ${formatTime(secondsLeft)}`
                : "Watch video to complete lesson"}
            </span>
          </div>
        </div>
        {effectiveIsWatched && (
          <span className="bg-emerald-50 text-emerald-700 border border-emerald-200/80 font-gilroyBold text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
            <Check className="w-3.5 h-3.5" /> Watched
          </span>
        )}
      </div>

      {/* Prominent Video Completed Alert Banner */}
      {effectiveIsWatched && (
        <div className="bg-white border border-gray-100 rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3.5 animate-fadeIn">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-xl bg-gray-900 text-white flex items-center justify-center flex-shrink-0 shadow-xs">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 stroke-[2.5]" />
            </div>
            <div>
              <span className="font-gilroyBold text-gray-900 text-[14px] block">
                Video Completed!
              </span>
              <p className="text-gray-600 text-[13px] m-0 mt-0.5 leading-relaxed">
                {hasQuiz
                  ? `Lesson ${lessonIndex + 1} video is completed! Attempt the quiz to test your understanding.`
                  : isLastLesson
                  ? "You have completed all orientation videos! Take the qualifying quiz to finish onboarding."
                  : `Lesson ${lessonIndex + 1} video is completed. You can start Lesson ${lessonIndex + 2} now.`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onNext}
            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700 text-white font-gilroyBold text-[13px] px-4 py-2.5 rounded-xl border-0 shadow-xs cursor-pointer transition flex items-center justify-center gap-1.5 flex-shrink-0"
          >
            <span>{hasQuiz ? `Attempt Lesson ${lessonIndex + 1} Quiz` : isLastLesson ? "Start Qualifying Quiz" : `Start Lesson ${lessonIndex + 2}`}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main Video & Content Card */}
      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm space-y-4">
        <div className="space-y-1">
          <h3 className="font-gilroyBold text-lg text-gray-900 m-0">
            {lesson.title}
          </h3>
          <p className="text-gray-600 text-[13px] leading-relaxed m-0">
            {lesson.description}
          </p>
        </div>

        <div className="w-full aspect-video rounded-xl overflow-hidden bg-black shadow-xs border border-gray-100">
          <iframe
            ref={iframeRef}
            src={lesson.embedUrl}
            title={lesson.title}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className={`pt-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-xl p-3.5 border ${
          effectiveIsWatched
            ? "bg-emerald-50/70 border-emerald-100"
            : "bg-gray-50 border-gray-100"
        }`}>
          <div className="text-left">
            <span className="font-gilroyBold text-gray-900 text-[13px] block">
              {effectiveIsWatched
                ? "Lesson Completed"
                : isLessonOneLocked
                ? "Watching Lesson 1 (Required: 7 mins)"
                : "Watching Lesson"}
            </span>
            <p className="text-gray-500 text-[13px] m-0 mt-0.5">
              {effectiveIsWatched
                ? isLastLesson
                  ? "All videos completed! Proceed to the Qualifying Quiz."
                  : `You've completed this video. Click below or use the step bar to start Lesson ${lessonIndex + 2}.`
                : isLessonOneLocked
                ? `Please watch the orientation lesson. Complete button will activate in ${formatTime(secondsLeft)}.`
                : "Click Complete Lesson below once you have finished watching."}
            </p>
          </div>
          {!effectiveIsWatched && (
            isLessonOneLocked ? (
              <button
                type="button"
                disabled
                className="text-gray-500 bg-gray-200/80 font-gilroyBold text-[13px] px-3.5 py-2 rounded-xl border border-gray-300 opacity-70 flex-shrink-0 cursor-not-allowed flex items-center gap-2"
              >
                <Lock className="w-3.5 h-3.5 text-gray-500" />
                <span>Complete in {formatTime(secondsLeft)}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={onMarkAsWatched}
                className="text-white bg-emerald-600 hover:bg-emerald-700 font-gilroyBold text-[13px] px-3.5 py-2 rounded-xl border-0 shadow-xs transition cursor-pointer flex-shrink-0 flex items-center gap-1.5"
              >
                <Check className="w-3.5 h-3.5" />
                <span>Complete Lesson</span>
              </button>
            )
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={onPrevious}
          className="flex-1 py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-gilroyBold text-[13px] rounded-xl border border-gray-200/80 shadow-xs transition cursor-pointer flex items-center justify-center gap-2"
        >
          <RotateCcw className="w-4 h-4 text-gray-500" />
          <span>Revision (Back)</span>
        </button>

        {effectiveIsWatched && (
          <button
            type="button"
            onClick={onNext}
            className="flex-1 py-3 px-4 bg-emerald-600 hover:bg-emerald-700 text-white font-gilroyBold text-[13px] rounded-xl border-0 shadow-xs cursor-pointer transition flex items-center justify-center gap-2 animate-fadeIn"
          >
            <span>{hasQuiz && !isQuizCompleted ? `Attempt Lesson ${lessonIndex + 1} Quiz` : isLastLesson ? "Proceed to Results" : `Start Lesson ${lessonIndex + 2}`}</span>
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default LessonPlayerView;
